import { FunctionComponent } from 'react';
import styled from 'styled-components';

import AlertWarningCircle from '@/components/atom/icon/AlertWarningCircle';
import ViewOff from '@/components/atom/icon/ViewOff';
import ThumbnailPresignedImage from '@/components/molecules/ThumbnailPresignedImage';
import { AttachmentBlindType } from '@/types/bff/gql-schema';
import { QnaDetailVo } from '@/types/qna';

import QnaAttachmentsSwiper, { ImageSwiperProps } from './QnaAttachmentsSwiper';

export interface QuestionAttachmentsProps
  extends Pick<ImageSwiperProps, 'imageSlideIdx' | 'onChangeImageBlindType' | 'onChangeSlideImage'> {
  questionDetail: QnaDetailVo;
  onClickImage: (index: number) => void;
  imageZoomIndex: number;
  onCloseImageZoom: () => void;
  attachments: QnaDetailVo['attachments'];
}
const QuestionAttachments: FunctionComponent<QuestionAttachmentsProps> = ({
  questionDetail,
  onClickImage,
  imageZoomIndex,
  onCloseImageZoom,
  attachments,
  imageSlideIdx,
  onChangeImageBlindType,
  onChangeSlideImage,
}) => {
  return (
    <Container>
      <ImageWrapper>
        {attachments.map((image, index) => {
          return (
            <ImageBox key={`image-${index}`} onClick={() => onClickImage(index)}>
              <>
                {/* 내 글 || (NONE/DIMMED) */}
                {(questionDetail.isMine || image.blindType !== AttachmentBlindType.Blinded) && (
                  <>
                    <QuestionImage
                      attachmentId={image.questionAttachmentId}
                      width={72}
                      height={72}
                      isDimmed={image.blindType === AttachmentBlindType.Dimmed}
                    />
                    {image.blindType === AttachmentBlindType.Dimmed && !questionDetail.isMine && <ImageDimmed />}
                  </>
                )}

                {/* 내 글x (DIMMED/BLINDED) */}
                {!questionDetail.isMine && image.blindType === AttachmentBlindType.Blinded && (
                  <IconWrapper>
                    <ViewOff />
                  </IconWrapper>
                )}

                {/* 내 글 (BLINDED) */}
                {questionDetail.isMine && image.blindType === AttachmentBlindType.Blinded && (
                  <MyQuestionBlind>
                    <AlertWarningCircle size={12} />
                    비공개
                  </MyQuestionBlind>
                )}
              </>
            </ImageBox>
          );
        })}
      </ImageWrapper>

      {/* 이미지 클릭시 확대 스와이프 */}
      {imageZoomIndex !== -1 && questionDetail && (
        <QnaAttachmentsSwiper
          initialIndex={imageZoomIndex}
          images={
            questionDetail.isMine
              ? attachments
              : attachments.filter((attachment) => attachment.blindType !== AttachmentBlindType.Blinded)
          }
          isMine={questionDetail.isMine}
          onClose={onCloseImageZoom}
          imageSlideIdx={imageSlideIdx}
          onChangeImageBlindType={onChangeImageBlindType}
          onChangeSlideImage={onChangeSlideImage}
        />
      )}
    </Container>
  );
};

export default QuestionAttachments;

const Container = styled.div``;
const ImageWrapper = styled.div`
  display: flex;
  overflow: scroll;
  margin-top: 20px;
`;

const ImageBox = styled.div`
  margin-right: 8px;
  overflow: hidden;
  border-radius: 8px;
  position: relative;
  min-width: 72px;
  height: 72px;
  display: flex;
  cursor: pointer;

  ${({ theme }) => `
    border: 1px solid ${theme.color.gray200};
    background: ${theme.color.gray50};
  `};
`;

const QuestionImage = styled(ThumbnailPresignedImage)<{ isDimmed: boolean }>`
  object-fit: cover;
  ${({ isDimmed }) => `
    ${
      isDimmed
        ? `
      filter: blur(4px);
      -webkit-filter: blur(4px);
      `
        : ''
    }
  `}
`;

const MyQuestionBlind = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 4px;
  bottom: 4px;
  border-radius: 4px;
  padding: 5px 8px;
  gap: 4px;

  ${({ theme }) => `
    ${theme.typography.UI12M};
    color: ${theme.color.yellow500};
    background: ${theme.color.dimmed40};
  `};
`;

const IconWrapper = styled.span`
  position: absolute;
  top: 50%;
  right: 50%;
  transform: translate(50%, -50%);
`;

const ImageDimmed = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.color.dimmed40};
  position: absolute;
  top: 0;
`;
